import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import TemuOpsListToolbar from '@/temu-platform/components/TemuOpsListToolbar';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import CategoryCascaderSelect from '@/temu-platform/components/CategoryCascaderSelect';
import TemuSwitch from '@/temu-platform/components/TemuSwitch';
import TemuOpsCopyHoverMenu from '@/temu-platform/components/TemuOpsCopyHoverMenu';
import TemuTemplateScopeTabs from '@/temu-platform/components/TemuTemplateScopeTabs';
import TemuStyleNoBatchFilter from '@/temu-platform/components/TemuStyleNoBatchFilter';
import TemuTemplateNameBatchFilter from '@/temu-platform/components/TemuTemplateNameBatchFilter';
import TemuGatewayRegionTabs, { TEMU_GATEWAY_REGION_ALL } from '@/temu-platform/components/TemuGatewayRegionTabs';
import AdminSheinOfficialBatchMoveSitesModal from '@/pages/admin/components/AdminSheinOfficialBatchMoveSitesModal';
import useMaterialTemplatePortableActions from '@/temu-platform/modules/templates/useMaterialTemplatePortableActions';
import { formatBeijingDateTime } from '@/temu-platform/utils/formatBeijingDateTime';
import { cellDisplayTitle } from '@/temu-platform/utils/cellDisplayTitle';
import { copyTemplateName } from '@/temu-platform/utils/temuTemplateCopyName';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import { MATERIAL_TEMPLATE_TYPES } from '@/temu-platform/utils/temuMaterialTemplatePortable';
import { listEnabledSites } from '@/temu-platform/utils/temuSiteList';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { matchTemplateName, templateNameTokens } from '@/temu-platform/utils/templateNameQuery';
import { formatTemplateSiteNames, templateMatchesGatewayRegion } from '@/temu-platform/utils/temuDeploymentRegions';
import { TEMU_MODE_FULL } from '@/temu-platform/utils/temuShopCapabilities';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import {
  buildTemuWorkspaceBatchOpsMenu,
  buildTemplateCopyMenuOptions,
  resolveCopySourceScope,
  resolveCopyTargetScope,
  resolveTemplateRowShopId,
  rowCanCopyScopedTemplate,
  rowCanDeleteScopedTemplate,
  rowCanEditScopedTemplate,
  rowCanPublishScopedTemplate,
  templateCopiedToast,
} from '@/temu-platform/constants/temuTemplateScope';
import { openTemuPathInNewTab } from '@/temu-platform/constants/temuRoutes';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/temu-platform/utils/temuTemplateVersionGuard';
import useTemuTemplateScope from '@/temu-platform/hooks/useTemuTemplateScope';
import { useTemuPageShopScope } from '@/temu-platform/hooks/useTemuPageShopScope';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import { usePlatformShopPick, resolveWriteShopId } from '@/hooks/usePlatformShopPick';
import { temuPlatformAPI } from '@/utils/api';
import { showToast } from '@/utils/toast';

function rowKey(row) {
  return String(row?.id ?? '');
}

/** 全托管不选站点，列表固定展示 all；半托管展示模板里勾选的站点。 */
function templateSiteNames(row, catalogSites, workspaceShopType) {
  const rowType = String(row?.shopType || '').trim();
  const mode = rowType || String(workspaceShopType || '').trim();
  if (mode === TEMU_MODE_FULL) return ['all'];
  return formatTemplateSiteNames(row, catalogSites);
}

/** 团队模板可能归属队友店铺，API 需用模板上的 shopId */
const resolveRowShopId = resolveTemplateRowShopId;

const COL_SPAN_FULL = 12;
const COL_SPAN_OFFICIAL = 8;

/** 模板库 · 产品模板（整份发品草稿快照，列表对齐属性库） */
export default function ProductTemplatePage() {
  const { t } = useTranslation('workbench');
  const { shopDimensionTabs, pageShopScope, scopedShops = [], expectedShopType } = useTemuPageShopScope();
  const {
    scopeTab,
    changeScopeTab,
    listShopId,
    listAllShops,
    canQuery,
    isOfficialTab,
    canAccessTeam,
    categoryOptions,
    categories,
  } = useTemuTemplateScope({ preferredShopId: pageShopScope });

  
  
  const { pickShop, shopPickModal } = usePlatformShopPick({
    shops: scopedShops,
    initialShopId: listShopId,
  });

  const ensureWriteShopId = useCallback(async ({
    title = t('productTemplate.pickShopTitle'),
    description = t('productTemplate.pickShopDesc'),
    confirmText = t('productTemplate.confirm'),
  } = {}) => resolveWriteShopId({
    listAllShops,
    pageShopScope,
    listShopId,
    pickShop,
    title,
    description,
    confirmText,
  }), [listAllShops, pageShopScope, listShopId, pickShop, t]);

const { isBusy } = useTemuSyncJobs();

  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [styleNoDraft, setStyleNoDraft] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [gatewayRegion, setGatewayRegion] = useState(TEMU_GATEWAY_REGION_ALL);
  const [catalogSites, setCatalogSites] = useState([]);
  const [categoryIdDraft, setCategoryIdDraft] = useState('');
  const [productTypeIdDraft, setProductTypeIdDraft] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [queryNonce, setQueryNonce] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [copyingId, setCopyingId] = useState(null);
  const [batchCopyingSync, setBatchCopyingSync] = useState(false);
  const [moving, setMoving] = useState(false);
  const [batchMovingSites, setBatchMovingSites] = useState(false);
  const [moveSitesOpen, setMoveSitesOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const tableColSpan = isOfficialTab ? COL_SPAN_OFFICIAL : COL_SPAN_FULL;

  useEffect(() => {
    if (!canQuery || !listShopId) {
      setCatalogSites([]);
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopSiteList(listShopId)
      .then((res) => {
        if (!cancelled) setCatalogSites(listEnabledSites(res?.siteList ?? res));
      })
      .catch(() => {
        if (!cancelled) setCatalogSites([]);
      });
    return () => { cancelled = true; };
  }, [canQuery, listShopId]);

  useEffect(() => {
    setGatewayRegion(TEMU_GATEWAY_REGION_ALL);
  }, [scopeTab, listShopId]);

  const applyLocalFilters = useCallback((rows) => {
    let list = Array.isArray(rows) ? rows : [];
    const nameTokens = templateNameTokens(keyword);
    if (nameTokens.length > 1) {
      list = list.filter((row) => matchTemplateName(row.name, nameTokens));
    }
    if (gatewayRegion && gatewayRegion !== TEMU_GATEWAY_REGION_ALL) {
      list = list.filter((row) => templateMatchesGatewayRegion(row, gatewayRegion));
    }
    return list;
  }, [keyword, gatewayRegion]);

  const load = useCallback(async () => {
    if (!canQuery || !listShopId) {
      setItems([]);
      setTotal(0);
      setSelectedKeys(new Set());
      return;
    }
    setLoading(true);
    try {
      const nameTokens = templateNameTokens(keyword);
      const needClientFilter = nameTokens.length > 1
        || (gatewayRegion && gatewayRegion !== TEMU_GATEWAY_REGION_ALL);
      const baseParams = {
        scope: scopeTab,
        styleNo: styleNo || undefined,
        productTypeId: productTypeId || undefined,
        ...(listAllShops ? { allShops: true } : {}),
      };
      if (needClientFilter) {
        const all = await fetchAllPages(async (pageNo, size) => {
          const res = await temuPlatformAPI.listProductTemplates(listShopId, {
            ...baseParams,
            keyword: nameTokens.length <= 1 ? (nameTokens[0] || undefined) : undefined,
            page: pageNo,
            pageSize: size,
          });
          return {
            items: Array.isArray(res?.items) ? res.items : [],
            total: Number(res?.total) || 0,
          };
        });
        const filtered = applyLocalFilters(all);
        const start = (Math.max(1, page) - 1) * pageSize;
        setItems(filtered.slice(start, start + pageSize));
        setTotal(filtered.length);
      } else {
        const res = await temuPlatformAPI.listProductTemplates(listShopId, {
          ...baseParams,
          keyword: nameTokens[0] || undefined,
          page,
          pageSize,
        });
        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotal(Number(res?.total) || 0);
      }
    } catch (e) {
      setItems([]);
      setTotal(0);
      showToast(e?.message || t('productTemplate.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [canQuery, listShopId, listAllShops, scopeTab, keyword, styleNo, gatewayRegion, productTypeId, page, pageSize, queryNonce, applyLocalFilters, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const map = selectedRowsRef.current;
    for (const id of [...map.keys()]) {
      if (!selectedKeys.has(id)) map.delete(id);
    }
    items.forEach((row) => {
      const key = rowKey(row);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
  }, [items, selectedKeys]);

  const selectableKeys = useMemo(
    () => items
      .filter((row) => rowCanDeleteScopedTemplate(row) || rowCanCopyScopedTemplate(row))
      .map(rowKey)
      .filter(Boolean),
    [items],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onCategoryChange = (id, meta) => {
    const nextCategoryId = id ? String(id) : '';
    const hit = categoryOptions.find((opt) => String(opt.value) === nextCategoryId);
    // 类目缓存只存一级，末级由级联懒加载返回，categoryOptions 里可能查不到 → 用 meta 兜底
    const metaTypeId = meta?.productTypeId != null && String(meta.productTypeId) !== '0'
      ? String(meta.productTypeId)
      : '';
    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
      ? String(hit.productTypeId)
      : (metaTypeId || nextCategoryId);
    setCategoryIdDraft(nextCategoryId);
    setProductTypeIdDraft(nextTypeId);
  };

  const runQuery = () => {
    setKeyword(keywordDraft.trim());
    setStyleNo(styleNoDraft.trim());
    setProductTypeId(productTypeIdDraft);
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resetQuery = () => {
    setKeywordDraft('');
    setKeyword('');
    setStyleNoDraft('');
    setStyleNo('');
    setCategoryIdDraft('');
    setProductTypeIdDraft('');
    setProductTypeId('');
    setPage(1);
    setQueryNonce((n) => n + 1);
  };

  const resolveSelectedRows = useCallback(() => {
    const map = selectedRowsRef.current;
    return Array.from(selectedKeys).map((key) => map.get(key)).filter(Boolean);
  }, [selectedKeys]);

  const resolvePageRows = useCallback(() => items, [items]);

  const resolveFilteredRows = useCallback(async () => {
    if (!canQuery || !listShopId) return [];
    const nameTokens = templateNameTokens(keyword);
    const baseParams = {
      scope: scopeTab,
      styleNo: styleNo || undefined,
      productTypeId: productTypeId || undefined,
      ...(listAllShops ? { allShops: true } : {}),
    };
    const all = await fetchAllPages(async (pageNo, size) => {
      const res = await temuPlatformAPI.listProductTemplates(listShopId, {
        ...baseParams,
        keyword: nameTokens.length <= 1 ? (nameTokens[0] || undefined) : undefined,
        page: pageNo,
        pageSize: size,
      });
      return {
        items: Array.isArray(res?.items) ? res.items : [],
        total: Number(res?.total) || 0,
      };
    });
    return applyLocalFilters(all);
  }, [canQuery, listShopId, listAllShops, scopeTab, keyword, styleNo, productTypeId, applyLocalFilters]);

  const {
    exporting,
    onExportCsvFiltered,
    onExportCsvPage,
    onExportCsvSelected,
  } = useMaterialTemplatePortableActions({
    type: MATERIAL_TEMPLATE_TYPES.products,
    shopId: listShopId,
    filePrefix: t('productTemplate.filePrefix'),
    resolvePageRows,
    resolveSelectedRows,
    resolveFilteredRows,
    buildCsvRows: (rows) => rows.map((row) => ({
      [t('productTemplate.colPriority')]: row.sortOrder ?? 0,
      [t('productTemplate.colStyleNo')]: row.styleNo || '',
      [t('productTemplate.colName')]: row.name || '',
      [t('productTemplate.colRemark')]: row.remark || '',
      [t('productTemplate.colCategory')]: row.categoryPathLabel || row.productTypeId || '',
      [t('productTemplate.site')]: templateSiteNames(row, catalogSites, expectedShopType).join('、'),
      [t('productTemplate.colAutoRule')]: row.autoRuleEnabled ? t('productTemplate.on') : t('productTemplate.off'),
      [t('productTemplate.colCreatedAt')]: formatBeijingDateTime(row.createdAt),
      [t('productTemplate.colUpdatedAt')]: formatBeijingDateTime(row.updatedAt),
      [t('productTemplate.colCreatedBy')]: row.createdByName || '',
      [t('productTemplate.colUpdatedBy')]: row.updatedByName || '',
    })),
    getOne: (id) => {
      const row = selectedRowsRef.current.get(String(id))
        || items.find((item) => String(item.id) === String(id));
      const sid = resolveRowShopId(row, listShopId);
      return temuPlatformAPI.getProductTemplate(sid, id, { scope: scopeTab });
    },
    listByProductType: async (productTypeId, ctx) => {
      const sid = ctx?.shopId || listShopId;
      const res = await temuPlatformAPI.listProductTemplates(sid, {
        scope: scopeTab,
        productTypeId: productTypeId || undefined,
        page: 1,
        pageSize: 200,
      });
      return Array.isArray(res?.items) ? res.items : [];
    },
    createOne: (body, ctx) => temuPlatformAPI.createProductTemplate(ctx?.shopId || listShopId, {
      ...body,
      scope: scopeTab,
    }),
    onImported: async () => {
      setSelectedKeys(new Set());
      setPage(1);
      setQueryNonce((n) => n + 1);
    },
  });

  const onBatchDelete = async () => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToDelete'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdDelete'), 'error');
      return;
    }
    const ok = await showTemuConfirm({
      title: t('returnList.batchDelete'),
      message: t('productTemplate.batchDeleteMsg', { count: ids.length }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await temuPlatformAPI.batchDeleteProductTemplates(listShopId, ids);
      const deleted = Number(res?.deleted);
      showToast(t('productTemplate.deletedCount', { count: Number.isFinite(deleted) ? deleted : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onBatchCopy = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToCopy'), 'warning');
      return;
    }
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('productTemplate.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdCopy'), 'error');
      return;
    }
    setBatchCopyingSync(true);
    try {
      const res = await temuPlatformAPI.batchCopyProductTemplates(writeShopId, {
        ids,
        sourceScope: resolveCopySourceScope(null, { isOfficialTab, scopeTab }),
        targetScope,
      });
      const copied = Number(res?.copied) || 0;
      const skipped = Number(res?.skipped) || 0;
      const skippedStyleNos = Array.isArray(res?.skippedStyleNos) ? res.skippedStyleNos : [];
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      if (copied > 0 && skipped > 0) {
        const sample = skippedStyleNos.slice(0, 5).join('、');
        const more = skippedStyleNos.length > 5 ? '…' : '';
        showToast(
          t('productTemplate.copiedSkipped', { copied, skipped, sample: `${sample}${more}` }),
          'warning',
          5000,
        );
      } else if (copied > 0) {
        showToast(t('productTemplate.copiedCount', { count: copied }), 'success');
      } else if (skipped > 0) {
        showToast(t('productTemplate.skippedSame', { count: skipped }), 'warning', 5000);
      } else {
        showToast(t('productTemplate.noneCopied'), 'warning', 5000);
      }
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchCopyFailed'), 'error');
    } finally {
      setBatchCopyingSync(false);
    }
  };

  const onBatchMove = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToMove'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdMove'), 'error');
      return;
    }
    setMoving(true);
    try {
      const res = await temuPlatformAPI.moveProductTemplates(listShopId, {
        ids,
        targetScope,
      });
      const moved = Number(res?.moved);
      showToast(t('productTemplate.movedCount', { count: Number.isFinite(moved) ? moved : ids.length }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchMoveFailed'), 'error');
    } finally {
      setMoving(false);
    }
  };

  const openBatchMoveSitesPicker = () => {
    if (!canQuery || isOfficialTab || selectedKeys.size === 0) {
      showToast(t('productTemplate.pickToMoveSites'), 'warning');
      return;
    }
    if (!resolveSelectedRows().length) {
      showToast(t('productTemplate.missingDataMove'), 'error');
      return;
    }
    setMoveSitesOpen(true);
  };

  const onBatchMoveSites = async (selectedSites) => {
    const sites = Array.isArray(selectedSites)
      ? selectedSites.map((s) => String(s || '').trim()).filter(Boolean)
      : [];
    if (!sites.length) {
      showToast(t('productTemplate.pickTargetSite'), 'warning');
      return;
    }
    if (isOfficialTab) {
      showToast(t('productTemplate.officialNoMove'), 'warning');
      return;
    }
    const rows = resolveSelectedRows().filter((row) => rowCanEditScopedTemplate(row));
    if (!rows.length) {
      showToast(t('productTemplate.noEditableMove'), 'error');
      return;
    }
    setBatchMovingSites(true);
    try {
      let updated = 0;
      for (const row of rows) {
        const body = { selectedSites: sites };
        const version = parseTemplateVersion(row.version);
        if (version != null) body.version = version;
        await temuPlatformAPI.updateProductTemplate(
          resolveRowShopId(row, listShopId),
          row.id,
          body,
        );
        updated += 1;
      }
      showToast(t('productTemplate.movedSites', { count: updated }), 'success');
      setMoveSitesOpen(false);
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchMoveSitesFailed'), 'error');
    } finally {
      setBatchMovingSites(false);
    }
  };

  const onCreateTemplate = async () => {
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.createPickTitle'),
      description: t('productTemplate.createPickDesc'),
      confirmText: t('productTemplate.createHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    openTemuPathInNewTab(
      `templates/product/new?scope=${encodeURIComponent(scopeTab)}&shopId=${encodeURIComponent(writeShopId)}`,
    );
  };

  const batchCopying = isBusy('productTemplateBatchCopy', listShopId) || batchCopyingSync;
  const batchBusy = deleting || exporting || batchCopying || moving || batchMovingSites;

  const onDeleteOne = async (row) => {
    if (!canQuery || !row?.id) return;
    const ok = await showTemuConfirm({
      title: t('productTemplate.deleteTitle'),
      message: t('productTemplate.deleteOne', { name: row.name || row.id }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await temuPlatformAPI.deleteProductTemplate(resolveRowShopId(row, listShopId), row.id);
      showToast(t('productTemplate.deleted'), 'success');
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        next.delete(rowKey(row));
        return next;
      });
      await load();
    } catch (e) {
      showToast(e?.message || t('productTemplate.deleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onCopyOne = async (row, targetScope) => {
    if (!canQuery || !row?.id || copyingId) return;
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('productTemplate.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const resolvedTarget = resolveCopyTargetScope(targetScope, { isOfficialTab, scopeTab });
    setCopyingId(row.id);
    try {
      await temuPlatformAPI.copyProductTemplate(writeShopId, {
        sourceId: row.id,
        sourceScope: resolveCopySourceScope(row, { isOfficialTab, scopeTab }),
        targetScope: resolvedTarget,
        name: copyTemplateName(row.name),
      });
      showToast(
        isOfficialTab
          ? `${templateCopiedToast(resolvedTarget)}${t('productTemplate.imageConverted', { platform: 'TEMU' })}`
          : templateCopiedToast(resolvedTarget),
        'success',
      );
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.copyFailed'), 'error');
    } finally {
      setCopyingId(null);
    }
  };

  const onToggleAutoRule = async (row, enabled) => {
    if (!canQuery || !row?.id || !rowCanEditScopedTemplate(row)) return;
    const id = row.id;
    const key = String(id);
    const prevEnabled = row.autoRuleEnabled === true || row.autoRuleEnabled === 1;
    setTogglingIds((prev) => new Set(prev).add(key));
    setItems((prev) => prev.map((item) => (
      String(item.id) === key ? { ...item, autoRuleEnabled: enabled } : item
    )));
    try {
      const body = { autoRuleEnabled: enabled };
      const version = parseTemplateVersion(row.version);
      if (version != null) body.version = version;
      const res = await temuPlatformAPI.updateProductTemplate(resolveRowShopId(row, listShopId), id, body);
      const nextVersion = parseTemplateVersion(res?.item?.version);
      setItems((prev) => prev.map((item) => (
        String(item.id) === key
          ? {
            ...item,
            autoRuleEnabled: enabled,
            ...(nextVersion != null ? { version: nextVersion } : {}),
          }
          : item
      )));
      showToast(enabled ? t('productTemplate.autoOn') : t('productTemplate.autoOff'), 'success');
    } catch (e) {
      setItems((prev) => prev.map((item) => (
        String(item.id) === key ? { ...item, autoRuleEnabled: prevEnabled } : item
      )));
      if (isTemplateVersionConflictError(e)) {
        showToast(conflictToastMessage([{ type: 'product' }]), 'warning', 3000);
        try {
          const res = await temuPlatformAPI.getProductTemplate(resolveRowShopId(row, listShopId), id, { scope: row.scope || scopeTab });
          const item = res?.item || res || {};
          const serverEnabled = item.autoRuleEnabled === true || item.autoRuleEnabled === 1;
          const serverVersion = parseTemplateVersion(item.version);
          setItems((prev) => prev.map((itemRow) => (
            String(itemRow.id) === key
              ? {
                ...itemRow,
                autoRuleEnabled: serverEnabled,
                ...(serverVersion != null ? { version: serverVersion } : {}),
              }
              : itemRow
          )));
        } catch {
          // 单行刷新失败时保持回滚状态
        }
      } else {
        showToast(e?.message || t('productTemplate.autoUpdateFailed'), 'error');
      }
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const emptyHint = !canQuery
    ? t('returnList.pickShop')
    : (loading
      ? t('returnList.loading')
      : (keyword || styleNo || (gatewayRegion && gatewayRegion !== TEMU_GATEWAY_REGION_ALL) || productTypeId
        ? t('productTemplate.noMatch')
        : (isOfficialTab ? t('productTemplate.emptyOfficial') : t('productTemplate.empty'))));

  return (
    <>
      <TemuSyncProgressSlot kinds={['productTemplateBatchCopy']} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || batchBusy}
            batchCopying={batchCopying}
            batchMoving={moving || batchMovingSites}
            batchOpsMenu={buildTemuWorkspaceBatchOpsMenu({
              scopeTab,
              isOfficialTab,
              canAccessTeam,
              selectedCount: selectedKeys.size,
              onCopy: onBatchCopy,
              onMove: onBatchMove,
              onDelete: onBatchDelete,
              deleteDisabled: !canQuery || batchBusy || isOfficialTab,
              ...(isOfficialTab ? {} : {
                sites: catalogSites,
                onMoveSites: onBatchMoveSites,
                onPickMultiSites: openBatchMoveSitesPicker,
                moveSitesDisabled: batchBusy,
              }),
            })}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!canQuery || loading || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: items.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: onExportCsvFiltered,
              onExportPage: onExportCsvPage,
              onExportSelected: onExportCsvSelected,
            })}
            extraActions={isOfficialTab ? [] : [
              {
                key: 'create',
                label: t('productTemplate.create'),
                tone: 'primary',
                disabled: !canQuery || loading || batchBusy,
                onClick: onCreateTemplate,
              },
            ]}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--category">
              <span className="tpo-field__label">{t('productTemplate.colCategory')}</span>
              <CategoryCascaderSelect
                shopId={listShopId}
                categories={categories}
                value={categoryIdDraft}
                disabled={!canQuery || loading}
                placeholder={!canQuery ? t('returnList.pickShop') : t('productTemplate.allCategories')}
                emptyText={!canQuery ? t('returnList.pickShop') : t('productTemplate.noCategories')}
                onChange={onCategoryChange}
              />
            </div>
            <TemuStyleNoBatchFilter
              value={styleNoDraft}
              disabled={!canQuery}
              onChange={setStyleNoDraft}
              onEnter={runQuery}
            />
            <TemuTemplateNameBatchFilter
              value={keywordDraft}
              disabled={!canQuery}
              onChange={setKeywordDraft}
              onEnter={runQuery}
            />
          </div>
          <div className="tpo-filter-bar__actions">
            <button
              type="button"
              className="tpo-btn tpo-btn--ghost tpo-btn--sm"
              disabled={!canQuery || loading}
              onClick={resetQuery}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={!canQuery || loading}
              onClick={runQuery}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={shopDimensionTabs}
        loading={Boolean(canQuery) && loading}
        loadingLabel={t('productTemplate.querying')}>
        <TemuTemplateScopeTabs
          scopeTab={scopeTab}
          canAccessTeam={canAccessTeam}
          disabled={!canQuery || loading}
          ariaLabel={t('productTemplate.scopeAria')}
          onChange={(tabId) => {
            changeScopeTab(tabId, {
              onReset: () => {
                setPage(1);
                setSelectedKeys(new Set());
                selectedRowsRef.current.clear();
                setQueryNonce((n) => n + 1);
              },
            });
          }}
        />
        <TemuGatewayRegionTabs
          shopId={listShopId}
          value={gatewayRegion}
          alwaysEnable
          hideForFull={false}
          onChange={(next) => {
            setGatewayRegion(next || TEMU_GATEWAY_REGION_ALL);
            setPage(1);
            setSelectedKeys(new Set());
            selectedRowsRef.current.clear();
            setQueryNonce((n) => n + 1);
          }}
        />
        <div className="tpo-drafts__table-wrap tpo-certs-table-wrap">
          <table
            className={`tpo-publish__table tpo-drafts__table tpo-certs-table tpo-tpl-table tpo-tpl-table--product${isOfficialTab ? ' tpo-tpl-table--product-official' : ''}`}
          >
            <colgroup>
              <col className="tpo-certs-col tpo-certs-col--check" />
              <col className="tpo-certs-col tpo-certs-col--order" />
              <col className="tpo-certs-col tpo-certs-col--style-no" />
              <col className="tpo-certs-col tpo-certs-col--name" />
              <col className="tpo-certs-col tpo-certs-col--category" />
              <col className="tpo-certs-col tpo-certs-col--sites" />
              <col className="tpo-certs-col tpo-certs-col--switch" />
              {!isOfficialTab ? (
                <>
                  <col className="tpo-certs-col tpo-certs-col--createdAt" />
                  <col className="tpo-certs-col tpo-certs-col--updatedAt" />
                  <col className="tpo-certs-col tpo-certs-col--user" />
                  <col className="tpo-certs-col tpo-certs-col--user" />
                </>
              ) : null}
              <col className="tpo-certs-col tpo-certs-col--ops" />
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-certs-th tpo-certs-th--check"
                  onClick={() => {
                    if (!canQuery || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('productTemplate.selectAll')}
                    disabled={!canQuery || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                <th scope="col" className="tpo-certs-th tpo-certs-th--order">{t('productTemplate.colPriority')}</th>
                <th scope="col" className="tpo-certs-th">{t('productTemplate.colStyleNo')}</th>
                <th scope="col" className="tpo-certs-th">{t('productTemplate.colName')}</th>
                <th scope="col" className="tpo-certs-th">{t('productTemplate.colCategory')}</th>
                <th scope="col" className="tpo-certs-th">{t('productTemplate.site')}</th>
                <th scope="col" className="tpo-certs-th tpo-certs-th--switch">{t('productTemplate.colAutoRule')}</th>
                {!isOfficialTab ? (
                  <>
                    <th scope="col" className="tpo-certs-th">{t('productTemplate.colCreatedAt')}</th>
                    <th scope="col" className="tpo-certs-th">{t('productTemplate.colUpdatedAt')}</th>
                    <th scope="col" className="tpo-certs-th">{t('productTemplate.colCreatedBy')}</th>
                    <th scope="col" className="tpo-certs-th">{t('productTemplate.colUpdatedBy')}</th>
                  </>
                ) : null}
                <th scope="col" className="tpo-certs-th tpo-certs-th--ops">{t('returnList.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!canQuery || loading || items.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">{emptyHint}</p>
                    </div>
                  </td>
                </tr>
              ) : items.map((row) => {
                const key = rowKey(row);
                const canEdit = rowCanEditScopedTemplate(row);
                const canDelete = rowCanDeleteScopedTemplate(row);
                const canPublish = rowCanPublishScopedTemplate(row);
                const canCopy = rowCanCopyScopedTemplate(row);
                const canSelect = canDelete || canCopy;
                const rowShopId = resolveRowShopId(row, listShopId);
                const siteNames = templateSiteNames(row, catalogSites, expectedShopType);
                const sitesLabel = siteNames.join('、');
                return (
                  <tr
                    key={key}
                    className={canSelect ? 'tpo-row--clickable' : undefined}
                    onClick={(e) => {
                      if (!canSelect) return;
                      if (e.target.closest('button, a, input, .tpo-copy-hover, .temu-draft-actions')) return;
                      toggleOne(key);
                    }}
                  >
                    <td className="tpo-certs-td tpo-certs-td--check">
                      <input
                        type="checkbox"
                        aria-label={t('returnList.selectRow', { title: row.name || key })}
                        disabled={!canSelect}
                        checked={selectedKeys.has(key)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => {
                          if (!canSelect) return;
                          toggleOne(key);
                        }}
                      />
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--order">{row.sortOrder ?? 0}</td>
                    <td className="tpo-certs-td" title={cellDisplayTitle(row.styleNo)}>{row.styleNo || '—'}</td>
                    <td className="tpo-certs-td" title={cellDisplayTitle(row.name, row.remark)}>
                      <strong>{row.name || '—'}</strong>
                      {row.remark ? <div className="tpo-muted">{row.remark}</div> : null}
                    </td>
                    <td className="tpo-certs-td" title={cellDisplayTitle(row.categoryPathLabel || row.productTypeId)}>
                      {row.categoryPathLabel || row.productTypeId || '—'}
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--sites" title={cellDisplayTitle(sitesLabel)}>
                      {siteNames.length ? (
                        <div className="tpo-tpl-sites">
                          {siteNames.map((name) => (
                            <div key={name} className="tpo-tpl-sites__item">{name}</div>
                          ))}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--switch">
                      <TemuSwitch
                        checked={!!row.autoRuleEnabled}
                        disabled={!canEdit || deleting || togglingIds.has(key)}
                        ariaLabel={t('productTemplate.autoRuleAria', { name: row.name || t('productTemplate.templateFallback') })}
                        onChange={(next) => onToggleAutoRule(row, next)}
                      />
                    </td>
                    {!isOfficialTab ? (
                      <>
                        <td className="tpo-certs-td tpo-certs-td--time" title={cellDisplayTitle(formatBeijingDateTime(row.createdAt))}>
                          {formatBeijingDateTime(row.createdAt)}
                        </td>
                        <td className="tpo-certs-td tpo-certs-td--time" title={cellDisplayTitle(formatBeijingDateTime(row.updatedAt))}>
                          {formatBeijingDateTime(row.updatedAt)}
                        </td>
                        <td className="tpo-certs-td" title={cellDisplayTitle(row.createdByName)}>{row.createdByName || '—'}</td>
                        <td className="tpo-certs-td" title={cellDisplayTitle(row.updatedByName)}>{row.updatedByName || '—'}</td>
                      </>
                    ) : null}
                    <td className="tpo-certs-td tpo-certs-td--ops">
                      <div className="temu-draft-actions">
                        {canPublish ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null}
                            onClick={() => openTemuPathInNewTab(
                              `products/publish?productTemplateId=${encodeURIComponent(row.id)}${rowShopId ? `&shopId=${encodeURIComponent(rowShopId)}` : ''}`,
                            )}
                          >
                            {t('productTemplate.publish')}
                          </button>
                        ) : null}
                        {canEdit ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null}
                            onClick={() => openTemuPathInNewTab(
                              `templates/product/edit/${encodeURIComponent(row.id)}?scope=${encodeURIComponent(row.scope || scopeTab)}${rowShopId ? `&shopId=${encodeURIComponent(rowShopId)}` : ''}`,
                            )}
                          >
                            {t('productTemplate.edit')}
                          </button>
                        ) : null}
                        {canCopy ? (
                          <TemuOpsCopyHoverMenu
                            disabled={deleting || copyingId != null}
                            busy={copyingId === row.id}
                            options={buildTemplateCopyMenuOptions({
                              scopeTab,
                              isOfficialTab,
                              canAccessTeam,
                              onCopy: (target) => onCopyOne(row, target),
                            })}
                          />
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            disabled={deleting || copyingId != null}
                            onClick={() => onDeleteOne(row)}
                          >
                            {t('returnList.delete')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('productTemplate.pager')}
        />
      </ProductListCard>

      <AdminSheinOfficialBatchMoveSitesModal
        open={moveSitesOpen}
        selectedCount={selectedKeys.size}
        sites={catalogSites}
        busy={batchMovingSites}
        onClose={() => {
          if (!batchMovingSites) setMoveSitesOpen(false);
        }}
        onConfirm={onBatchMoveSites}
      />
      {shopPickModal}
    </>
  );
}
